/**
 * Hook that initializes FCM push notifications on Capacitor.
 *
 * - Registers with FCM after authentication
 * - Sends the device token to the backend
 * - Handles notification taps by navigating to the relevant page
 *
 * Mount once at the app level (e.g., in GlobalNotificationHandler).
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { isCapacitor } from '../api/config';
import { initPushNotifications, resolvePushNavigationUrl } from './pushNotifications';
import axiosInstance from '../api/axiosInstance';

export function usePushNotifications(): void {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const registeredRef = useRef(false);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    if (!isCapacitor || !isAuthenticated || registeredRef.current) return;
    registeredRef.current = true;

    const platform = /android/i.test(navigator.userAgent) ? 'android' : 'ios';

    initPushNotifications(
      // onTokenReceived — send to backend
      async (token: string) => {
        try {
          await axiosInstance.post('/api/v1/push/register-token', {
            token,
            platform,
          });
        } catch (err) {
          console.error('Failed to register push token:', err);
        }
      },
      // onNotificationReceived (foreground) — no-op, WebSocket handles these
      undefined,
      // onNotificationTapped (background) — deep-link via the Entity URL Registry,
      // falling back to the app home so a tap always foregrounds the app.
      (data: Record<string, string>) => {
        navigateRef.current(resolvePushNavigationUrl(data) ?? '/');
      },
    );
  }, [isAuthenticated]);
}
